import { Copy, Download, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { CampoTexto } from '@/componentes/Campo';
import { CodigoDeSeisDigitos } from '@/componentes/CodigoDeSeisDigitos';
import { codigoCompleto } from '@/componentes/codigo-de-seis-digitos';
import { Barra } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { Janela } from '@/componentes/JanelaDeAcao';
import { formatarData, formatarInteiro } from '@/lib/formato';
import { EXEMPLO_DO_MOTIVO, motivoValido } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { anunciar } from '@/lib/retorno';
import { primeiraMaiuscula } from '@/lib/texto';
import {
  chaveEmGrupos,
  codigosComoTexto,
  useConfirmarSegundoFator,
  useDesativarSegundoFatorPelaSenha,
  useIniciarSegundoFator,
  useSegundoFator,
  useTrocarCodigosDeRecuperacao,
  type ChaveDoSegundoFator,
} from '@/modulos/sessao/segundo-fator';

/**
 * O SEGUNDO FATOR no Meu perfil da mesa (0085; desenho da R06, p.68).
 *
 * Quatro estados, cada um no mesmo cartão, com o selo no canto:
 *
 *  - **Desativado**: "Protege a conta mesmo se a senha vazar." e o primário
 *    "Ativar segundo fator".
 *  - **Ativando**: passo 1, a chave em mono com Copiar; passo 2, o código em
 *    seis campos; Cancelar · Confirmar código.
 *  - **Códigos de recuperação**: os dez, numerados, mostrados só agora, com
 *    Baixar · Copiar · Já guardei.
 *  - **Ativo**: "Ativado em 06/10/2026. Restam 9 códigos de recuperação." e
 *    Gerar códigos novos · Desativar. Gerar códigos novos pede o código do
 *    aplicativo (invalida os antigos); Desativar pede a senha e um motivo
 *    (p.69), numa confirmação.
 *
 * NAO HA QR NESTA TELA: o sistema nao tem gerador de QR, e mandar a chave a
 * um servico de fora para desenhar o QR a entregaria a quem nao deve. A
 * pessoa digita a chave no aplicativo ("inserir chave"), ou abre o endereco
 * `otpauth://` no telefone, que leva direto ao aplicativo.
 */
export function SegundoFator({ conta }: { readonly conta: string }) {
  const situacao = useSegundoFator();
  const [chave, setChave] = useState<ChaveDoSegundoFator | null>(null);
  const [codigos, setCodigos] = useState<readonly string[] | null>(null);

  if (situacao.isPending) {
    return (
      <Cartao titulo="Segundo fator">
        <div aria-hidden="true" className="flex flex-col gap-2">
          <Barra className="my-1 h-3 w-3/4" />
          <Barra className="my-1 h-3 w-1/2" />
        </div>
      </Cartao>
    );
  }
  if (situacao.isError) {
    return (
      <Cartao titulo="Segundo fator">
        <ErroDaLista
          oQue="O segundo fator"
          erro={situacao.error}
          aoTentarDeNovo={() => {
            void situacao.refetch();
          }}
        />
      </Cartao>
    );
  }

  if (codigos !== null) {
    return (
      <CodigosDeRecuperacao
        conta={conta}
        codigos={codigos}
        aoGuardar={() => {
          setCodigos(null);
        }}
      />
    );
  }
  if (chave !== null) {
    return (
      <Ativando
        conta={conta}
        chave={chave}
        aoCancelar={() => {
          setChave(null);
        }}
        aoAtivar={(c) => {
          setChave(null);
          setCodigos(c);
        }}
      />
    );
  }
  if (!situacao.data.ativo) return <Desativado aoIniciar={setChave} />;
  return (
    <Ativo
      conta={conta}
      ativadoEm={situacao.data.ativadoEm}
      restantes={situacao.data.codigosRestantes}
      aoTrocar={setCodigos}
    />
  );
}

/* ------------------------------------------------------------------ cartão */

type Situacao = 'Desativado' | 'Ativando' | 'Ativo';

const TOM_DA_SITUACAO: Record<Situacao, { readonly selo: string; readonly ponto: string; readonly bloco: string; readonly icone: string }> = {
  Desativado: { selo: 'bg-nao-alocado-fundo text-nao-alocado-texto', ponto: 'bg-nao-alocado', bloco: 'bg-rebaixada', icone: 'text-tinta-secundaria' },
  Ativando: { selo: 'bg-atencao-fundo text-atencao-texto', ponto: 'bg-atencao', bloco: 'bg-rebaixada', icone: 'text-tinta-secundaria' },
  Ativo: { selo: 'bg-sucesso-fundo text-sucesso-texto', ponto: 'bg-sucesso', bloco: 'bg-sucesso-fundo', icone: 'text-sucesso' },
};

function Cartao({ titulo, situacao, children }: { readonly titulo: string; readonly situacao?: Situacao; readonly children: ReactNode }) {
  const tom = situacao ? TOM_DA_SITUACAO[situacao] : TOM_DA_SITUACAO.Desativado;
  return (
    <section aria-label={titulo} className="cartao flex flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className={`flex size-[var(--indicador-bloco)] shrink-0 items-center justify-center rounded-controle ${tom.bloco}`}>
          <ShieldCheck className={`size-[var(--indicador-icone)] ${tom.icone}`} />
        </span>
        <h2 className="txt-secao min-w-0 flex-1 text-tinta">{titulo}</h2>
        {situacao ? (
          <span className={`inline-flex h-[var(--selo-altura)] shrink-0 items-center gap-1.5 rounded-pill px-2 ${tom.selo}`}>
            <span aria-hidden="true" className={`size-[var(--selo-ponto)] rounded-pill ${tom.ponto}`} />
            <span className="txt-selo">{situacao}</span>
          </span>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function Recusa({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
      <span className="txt-corpo text-tinta">{primeiraMaiuscula(f.erro)}</span>
      <span className="txt-apoio text-tinta-secundaria">{f.comoResolver}</span>
    </div>
  );
}

function copiar(texto: string, aoCopiar: () => void, aoFalhar: () => void) {
  const escrever = navigator.clipboard?.writeText.bind(navigator.clipboard);
  (escrever ? escrever(texto) : Promise.reject(new Error('sem área de transferência'))).then(aoCopiar, aoFalhar);
}

/* -------------------------------------------------------------- desativado */

function Desativado({ aoIniciar }: { readonly aoIniciar: (c: ChaveDoSegundoFator) => void }) {
  const iniciar = useIniciarSegundoFator();
  return (
    <Cartao titulo="Segundo fator" situacao="Desativado">
      <p className="txt-corpo max-w-[72ch] text-pretty text-tinta-secundaria">
        Protege a conta mesmo se a senha vazar. Depois da senha, a entrada pede os seis dígitos de um aplicativo
        autenticador do seu telefone. A biometria do app do celular não pede o código, porque ela já é um fator forte.
      </p>
      {iniciar.isError ? <Recusa erro={iniciar.error} /> : null}
      <div>
        <Botao
          nivel="primario"
          carregando={iniciar.isPending}
          onClick={() => {
            iniciar.mutate(undefined, { onSuccess: aoIniciar });
          }}
        >
          {iniciar.isPending ? 'Gerando a chave…' : 'Ativar segundo fator'}
        </Botao>
      </div>
    </Cartao>
  );
}

/* ---------------------------------------------------------------- ativando */

function Passo({ numero, titulo }: { readonly numero: number; readonly titulo: string }) {
  return (
    <p className="flex items-center gap-2">
      <span aria-hidden="true" className="flex size-6 shrink-0 items-center justify-center rounded-pill bg-preto-obra txt-legenda text-branco">
        {numero}
      </span>
      <span className="txt-subtitulo text-tinta">{titulo}</span>
    </p>
  );
}

function Ativando({
  conta,
  chave,
  aoCancelar,
  aoAtivar,
}: {
  readonly conta: string;
  readonly chave: ChaveDoSegundoFator;
  readonly aoCancelar: () => void;
  readonly aoAtivar: (codigos: readonly string[]) => void;
}) {
  const confirmar = useConfirmarSegundoFator(conta);
  const [codigo, setCodigo] = useState('');
  const [errou, setErrou] = useState(false);

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    if (!codigoCompleto(codigo) || confirmar.isPending) return;
    confirmar.mutate(codigo, {
      onSuccess: (r) => {
        aoAtivar(r.codigos);
      },
      onError: () => {
        /* Código errado (p.68): os seis campos ficam vermelhos e esvaziam. */
        setErrou(true);
        setCodigo('');
      },
    });
  };

  return (
    <Cartao titulo="Ativar segundo fator" situacao="Ativando">
      <form className="flex flex-col gap-4" onSubmit={enviar}>
        <Passo numero={1} titulo="Leve esta chave ao aplicativo" />
        <div className="bloco-rebaixado flex items-center justify-between gap-3 py-2 pr-2 pl-4">
          <span className="codigo min-w-0 text-tinta [overflow-wrap:anywhere]">{chaveEmGrupos(chave.chave)}</span>
          <Botao
            nivel="fantasma"
            tamanho="p"
            onClick={() => {
              copiar(
                chave.chave,
                () => {
                  anunciar({ tom: 'sucesso', titulo: 'Chave copiada', alvo: { nome: chave.conta }, fatos: ['Cole no aplicativo autenticador, em Inserir chave'] });
                },
                () => {
                  anunciar({ tom: 'falha', titulo: 'Chave não copiada', alvo: { nome: chave.conta }, comoResolver: 'Selecione a chave com o mouse e copie, ou digite no aplicativo.' });
                },
              );
            }}
          >
            <Copy aria-hidden="true" className="size-[var(--icone-controle)]" />
            Copiar
          </Botao>
        </div>
        <p className="txt-apoio max-w-[72ch] text-pretty text-tinta-secundaria">
          Em Google Authenticator, Microsoft Authenticator ou similar: Adicionar › Inserir chave, com a conta{' '}
          <span className="text-tinta">{chave.conta}</span>. No telefone,{' '}
          <a href={chave.endereco} className="text-tinta-secundaria underline">
            abra a chave direto no aplicativo
          </a>
          .
        </p>

        <Passo numero={2} titulo="Digite o código que apareceu" />
        <CodigoDeSeisDigitos
          rotulo="Código do aplicativo"
          valor={codigo}
          erro={errou}
          aoMudar={(v) => {
            setErrou(false);
            confirmar.reset();
            setCodigo(v);
          }}
        />
        {confirmar.isError ? <Recusa erro={confirmar.error} /> : null}
        <div className="flex flex-wrap justify-end gap-3">
          <Botao
            nivel="fantasma"
            onClick={() => {
              confirmar.reset();
              aoCancelar();
            }}
          >
            Cancelar
          </Botao>
          <Botao nivel="secundario" type="submit" disabled={!codigoCompleto(codigo)} carregando={confirmar.isPending}>
            {confirmar.isPending ? 'Conferindo…' : 'Confirmar código'}
          </Botao>
        </div>
      </form>
    </Cartao>
  );
}

/* ---------------------------------------------------- códigos de recuperação */

function CodigosDeRecuperacao({
  conta,
  codigos,
  aoGuardar,
}: {
  readonly conta: string;
  readonly codigos: readonly string[];
  readonly aoGuardar: () => void;
}) {
  const texto = codigosComoTexto(codigos);
  return (
    <Cartao titulo="Códigos de recuperação" situacao="Ativo">
      <p className="flex items-start gap-2 rounded-controle bg-atencao-fundo p-3 txt-apoio text-atencao-texto">
        <TriangleAlert aria-hidden="true" className="mt-0.5 size-[var(--icone-controle)] shrink-0 text-atencao" />
        Mostrados só agora. Guarde fora do celular. Cada código vale uma vez.
      </p>
      <ol aria-label="Códigos de recuperação" className="bloco-rebaixado grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-6 gap-y-2 p-4">
        {codigos.map((c, i) => (
          <li key={c} className="flex items-baseline gap-3">
            <span className="codigo text-tinta-apagada tabular-nums">{String(i + 1).padStart(2, '0')}</span>
            <span className="codigo text-tinta">{c.toUpperCase()}</span>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-3">
        <Botao
          nivel="secundario"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'codigos-de-recuperacao-ct-frotas.txt';
            a.click();
            URL.revokeObjectURL(url);
            anunciar({ tom: 'sucesso', titulo: 'Códigos baixados', alvo: { nome: conta }, fatos: [`${formatarInteiro(codigos.length)} códigos`, 'codigos-de-recuperacao-ct-frotas.txt'] });
          }}
        >
          <Download aria-hidden="true" className="size-[var(--icone-controle)]" />
          Baixar
        </Botao>
        <Botao
          nivel="secundario"
          onClick={() => {
            copiar(
              texto,
              () => {
                anunciar({ tom: 'sucesso', titulo: 'Códigos copiados', alvo: { nome: conta }, fatos: [`${formatarInteiro(codigos.length)} códigos`] });
              },
              () => {
                anunciar({ tom: 'falha', titulo: 'Códigos não copiados', alvo: { nome: conta }, comoResolver: 'Use Baixar, ou selecione os códigos com o mouse e copie.' });
              },
            );
          }}
        >
          <Copy aria-hidden="true" className="size-[var(--icone-controle)]" />
          Copiar
        </Botao>
        <Botao nivel="primario" className="ml-auto" onClick={aoGuardar}>
          Já guardei
        </Botao>
      </div>
    </Cartao>
  );
}

/* -------------------------------------------------------------------- ativo */

function Ativo({
  conta,
  ativadoEm,
  restantes,
  aoTrocar,
}: {
  readonly conta: string;
  readonly ativadoEm: string | null;
  readonly restantes: number;
  readonly aoTrocar: (codigos: readonly string[]) => void;
}) {
  const trocar = useTrocarCodigosDeRecuperacao(conta);
  const desativar = useDesativarSegundoFatorPelaSenha(conta);
  const [gerando, setGerando] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [errou, setErrou] = useState(false);
  const [desligando, setDesligando] = useState(false);
  const [senha, setSenha] = useState('');
  const [motivo, setMotivo] = useState('');

  const fecharDesligar = () => {
    setDesligando(false);
    setSenha('');
    setMotivo('');
    desativar.reset();
  };

  return (
    <Cartao titulo="Segundo fator" situacao="Ativo">
      <p className="txt-corpo text-tinta-secundaria">
        {ativadoEm ? `Ativado em ${formatarData(new Date(ativadoEm))}. ` : ''}
        {restantes === 1 ? 'Resta 1 código de recuperação.' : `Restam ${formatarInteiro(restantes)} códigos de recuperação.`}
      </p>

      {gerando ? (
        <form
          className="flex flex-col gap-3 border-t border-borda-sutil pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!codigoCompleto(codigo) || trocar.isPending) return;
            trocar.mutate(codigo, {
              onSuccess: (r) => {
                setGerando(false);
                setCodigo('');
                aoTrocar(r.codigos);
              },
              onError: () => {
                setErrou(true);
                setCodigo('');
              },
            });
          }}
        >
          <p className="txt-apoio text-tinta-secundaria">
            Para gerar códigos novos, digite o código do aplicativo. Os códigos antigos deixam de valer.
          </p>
          <CodigoDeSeisDigitos
            rotulo="Código do aplicativo"
            valor={codigo}
            erro={errou}
            focarAoAbrir
            aoMudar={(v) => {
              setErrou(false);
              trocar.reset();
              setCodigo(v);
            }}
          />
          {trocar.isError ? <Recusa erro={trocar.error} /> : null}
          <div className="flex flex-wrap justify-end gap-3">
            <Botao
              nivel="fantasma"
              onClick={() => {
                setGerando(false);
                setCodigo('');
                trocar.reset();
              }}
            >
              Cancelar
            </Botao>
            <Botao nivel="secundario" type="submit" disabled={!codigoCompleto(codigo)} carregando={trocar.isPending}>
              Gerar códigos novos
            </Botao>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-3">
            <Botao
              nivel="secundario"
              onClick={() => {
                setGerando(true);
              }}
            >
              <RefreshCw aria-hidden="true" className="size-[var(--icone-controle)]" />
              Gerar códigos novos
            </Botao>
            <Botao
              nivel="destrutivo"
              onClick={() => {
                setDesligando(true);
              }}
            >
              Desativar
            </Botao>
          </div>
          <p className="txt-apoio text-tinta-apagada">Gerar códigos novos invalida os antigos. Desativar pede a senha e um motivo.</p>
        </div>
      )}

      <Janela
        titulo="Desativar o segundo fator?"
        largura="p"
        aberta={desligando}
        aoFechar={fecharDesligar}
        acoes={
          <>
            <Botao nivel="secundario" onClick={fecharDesligar}>
              Manter ativado
            </Botao>
            <button
              type="button"
              className="botao botao-destrutivo-solido"
              disabled={senha === '' || !motivoValido(motivo) || desativar.isPending}
              onClick={() => {
                desativar.mutate({ senha, motivo: motivo.trim() }, { onSuccess: fecharDesligar });
              }}
            >
              {desativar.isPending ? 'Desativando…' : 'Desativar'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {desativar.isError ? <Recusa erro={desativar.error} /> : null}
          <p className="txt-corpo text-pretty text-tinta-secundaria">
            A entrada volta a pedir só a senha, e os códigos de recuperação deixam de valer. Para ativar de novo, a chave é
            outra.
          </p>
          <CampoTexto rotulo="Sua senha" tipo="senha" autoComplete="current-password" maxLength={200} valor={senha} aoMudar={setSenha} />
          <CampoDoMotivo valor={motivo} exemplo={`Por exemplo: ${EXEMPLO_DO_MOTIVO['segundo_fator.desativar']}`} aoMudar={setMotivo} />
        </div>
      </Janela>
    </Cartao>
  );
}
